import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";

import { faLinkedinIn } from "@fortawesome/free-brands-svg-icons";

import { connectionAutoUpdateOf } from "@signalist/shared-types";

import { useAppSelector } from "@/redux/hooks";
import { authSelector } from "@/pages/Public/redux/auth.slice";

import { SettingsCard } from "./SettingsCard";
import { ConnectedBadge } from "./SettingsSection";
import { LinkedinLogin } from "./LinkedinLogin";
import { LinkedinSyncSection } from "./LinkedinSyncSection";
import { LinkedinCapabilitiesCard } from "./LinkedinCapabilitiesCard";
import { AutoWithdrawInvites } from "./AutoWithdrawInvites";

/**
 * The LinkedIn card on the Connected Accounts tab. One card holding the Connection
 * (login/reconnect/disconnect) and — once connected — the Connection-syncing sections,
 * matching the design mockup.
 */
export const LinkedinCard: FunctionComponent = () => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`account.connectedAccounts.${key}`);

	const { user } = useAppSelector(authSelector);

	const isLoggedIn = !!user?.unipile?.isLoggedIn;

	return (
		<SettingsCard
			headerRight={
				<ConnectedBadge
					connected={isLoggedIn}
					label={isLoggedIn ? ts("connected") : ts("notConnected")}
				/>
			}
			icon={faLinkedinIn}
			subtitle={ts("linkedinSubtitle")}
			title={ts("linkedinTitle")}
		>
			<div className="flex flex-col gap-5 px-5 py-5">
				<LinkedinLogin />

				{isLoggedIn && (
					<>
						<LinkedinCapabilitiesCard />

						<LinkedinSyncSection autoUpdate={connectionAutoUpdateOf(user)} />

						<AutoWithdrawInvites
							autoWithdrawInvitesDays={user?.autoWithdrawInvitesDays}
							autoWithdrawInvitesEnabled={user?.autoWithdrawInvitesEnabled || false}
						/>
					</>
				)}
			</div>
		</SettingsCard>
	);
};
